export type SurveyReportStatus = 'WAITING_ANALYSES' | 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type SurveyReportAiStatus = 'completed' | 'processing' | 'failed' | 'missing' | 'outdated' | 'no_answers';

export type SurveyReportMode = 'now' | 'missing' | 'update' | 'selected' | 'regenerate';

export type SurveyReportRevisionMode = 'now' | 'missing' | 'selected' | 'regenerate';

export type SurveyReportRevisionState = 'saved' | 'ai_ready' | 'processing' | 'failed' | 'missing';

export type SurveyReportIssueType = 'failed' | 'timeout' | 'processing' | 'missing' | 'outdated' | 'render_error';

export interface SurveyReportIssues {
  counts: Partial<Record<SurveyReportIssueType, number>>;
  items: { question_id: number; question: string; type: SurveyReportIssueType; message: string | null }[];
}

export type SurveyReportAccentColor = 'purple' | 'blue' | 'green' | 'graphite' | 'orange';

export type SurveyReportAiType = 'supershort' | 'short' | 'full';

export type SurveyReportAiLayout = 'side' | 'separate';

export interface SurveyReportOptions {
  language: 'en' | 'it';
  title: string | null;
  subtitle: string | null;
  accent_color: SurveyReportAccentColor;
  footer_text: string | null;
  include_slide_numbers: boolean;
  include_overview: boolean;
  include_section_status: boolean;
  include_index: boolean;
  include_decisions: boolean;
  include_revised_statements: boolean;
  revised_statements_placeholder: boolean;
  include_section_divider: boolean;
  include_question_numbers: boolean;
  include_descriptions: boolean;
  include_ai_analysis: boolean;
  ai_analysis_type: SurveyReportAiType;
  ai_analysis_layout: SurveyReportAiLayout;
  include_additional_charts: boolean;
  skip_unanswered: boolean;
  typologies: string[] | null;
}

export const DEFAULT_SURVEY_REPORT_OPTIONS: SurveyReportOptions = {
  language: 'en',
  title: null,
  subtitle: null,
  accent_color: 'purple',
  footer_text: null,
  include_slide_numbers: true,
  include_overview: true,
  include_section_status: true,
  include_index: true,
  include_decisions: true,
  include_revised_statements: true,
  revised_statements_placeholder: false,
  include_section_divider: true,
  include_question_numbers: true,
  include_descriptions: false,
  include_ai_analysis: true,
  ai_analysis_type: 'short',
  ai_analysis_layout: 'side',
  include_additional_charts: true,
  skip_unanswered: false,
  typologies: null,
};

export const SURVEY_REPORT_ACCENT_COLORS: { id: SurveyReportAccentColor; hex: string }[] = [
  { id: 'purple', hex: '#7B55C8' },
  { id: 'blue', hex: '#1F6FB2' },
  { id: 'green', hex: '#2E7D5B' },
  { id: 'graphite', hex: '#3A3F4B' },
  { id: 'orange', hex: '#C2410C' },
];

export type SurveyReportTrackingAction = 'generate' | 'update' | 'regenerate' | 'wait';

export type SurveyReportTrackingState = 'pending' | 'processing' | 'completed' | 'failed';

export interface SurveyReportTrackingItem {
  label: string;
  action: SurveyReportTrackingAction;
  state: SurveyReportTrackingState;
  updated_at: string;
}

export interface SurveyReportTrackingEvent {
  at: string;
  type: string;
  params: Record<string, any>;
}

export interface SurveyReportTracking {
  analyses: Record<string, SurveyReportTrackingItem>;
  revisions: Record<string, SurveyReportTrackingItem>;
  events: SurveyReportTrackingEvent[];
}

export interface SurveyReport {
  id: number;
  status: SurveyReportStatus;
  requested_by?: string | null;
  tracking?: SurveyReportTracking | null;
  analyses_requested_at?: string | null;
  updated_at?: string;
  issues?: SurveyReportIssues;
  options?: SurveyReportOptions | null;
  progress_done: number;
  progress_total: number;
  file_name: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface SurveyReportQuestionStatus {
  id: number;
  question: string;
  typology: string;
  ai_status: SurveyReportAiStatus;
  answers_count: number;
  analysed_answers_count: number | null;
  error_reason: 'timeout' | 'error' | null;
  error_message: string | null;
}

export interface SurveyReportAnalysisStatus {
  questions: SurveyReportQuestionStatus[];
  counts: Record<SurveyReportAiStatus, number> & { total: number };
}

export const ACTIVE_REPORT_STATUSES: SurveyReportStatus[] = ['WAITING_ANALYSES', 'PENDING', 'PROCESSING'];

export interface SurveyReportRevisionQuestion {
  id: number;
  question: string;
  typology: string;
  final_decision: string | null;
  expected: 'split' | 'single' | 'any';
  revision_status: SurveyReportRevisionState;
  source: 'saved' | 'ai' | null;
  statements: string[];
  mismatch: boolean;
  in_flight: boolean;
  error_message: string | null;
}

export interface SurveyReportRevisionStatus {
  questions: SurveyReportRevisionQuestion[];
  counts: Record<SurveyReportRevisionState, number> & { total: number };
}

export interface SurveyReportDialogResult {
  mode: SurveyReportMode;
  questionIds: number[];
  options: SurveyReportOptions;
  revisionMode: SurveyReportRevisionMode;
  revisionQuestionIds: number[];
}
